<script setup lang="ts">
import { computed } from 'vue';

/**
 * Chip "Emplacements de bâtiments" de la barre header. Affiché dans la cellule
 * « Vie & énergie » (2e ligne) pour l'équilibrer : c'est une métrique
 * PAR-PLANÈTE et vitale (capacité d'accueil de la planète), cohérente avec la
 * grille per-planète.
 *
 * used  = Σ BuildingDefinition.Places des bâtiments non-QG (cf MeService).
 * total = Planet.Places (capacité recalculée par PlanetCapacityService).
 *
 * Visuellement aligné sur ResourceChip (.rc-chip) : même taille / mode fluid.
 * Vire au warning quand la planète est pleine (plus d'emplacement libre).
 */
const props = defineProps<{
  used: number;
  total: number;
  /** Mode fluide : la chip s'étire (flex 1) comme les ResourceChip voisines. */
  fluid?: boolean;
}>();

const free = computed(() => Math.max(0, props.total - props.used));
const isFull = computed(() => props.total > 0 && props.used >= props.total);
const fillPercent = computed(() => {
  if (props.total <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((props.used / props.total) * 100)));
});
</script>

<template>
  <AppInfoCard placement="bottom" :width="240">
    <div class="rc-chip pc-chip" :class="{ 'rc-fluid': fluid, 'is-full': isFull }">
      <el-icon class="pc-icon"><OfficeBuilding /></el-icon>
      <span class="rc-amount text-mono">{{ used }}/{{ total }}</span>
    </div>
    <template #content>
      <div class="tt-card">
        <div class="tt-head">
          <el-icon class="tt-head-icon"><OfficeBuilding /></el-icon>
          <span class="tt-name">Emplacements</span>
        </div>
        <ul class="tt-rows">
          <li class="tt-row">
            <span class="tt-label">Occupés</span>
            <span class="tt-value">{{ used }}</span>
          </li>
          <li class="tt-row">
            <span class="tt-label">Total</span>
            <span class="tt-value">{{ total }}</span>
          </li>
          <li class="tt-row">
            <span class="tt-label">Libres</span>
            <span class="tt-value" :class="isFull ? 'is-neg' : 'is-pos'">{{ free }}</span>
          </li>
        </ul>
        <div class="tt-bar" :class="{ 'is-sat': isFull }">
          <div class="tt-bar-fill" :style="{ width: `${fillPercent}%` }" />
        </div>
        <p v-if="isFull" class="tt-warn">
          Planète pleine : démolissez ou ne construisez plus de nouveaux bâtiments.
        </p>
        <p class="tt-note">
          Chaque bâtiment occupe ses emplacements quel que soit son niveau (le QG est exclu).
        </p>
      </div>
    </template>
  </AppInfoCard>
</template>

<style lang="scss" scoped>
// Reprend la chip ressource (.rc-chip) pour un alignement parfait avec les
// ResourceChip voisines : même taille fixe, même mode fluide.
.rc-chip {
  --rc-accent: 34, 197, 94; // vert (vie) par défaut
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  flex: 0 0 auto;
  width: 112px;
  height: 26px;
  padding: 2px 6px 2px 6px;
  background: rgba(var(--rc-accent), 0.10);
  border: 1px solid rgba(var(--rc-accent), 0.38);
  border-radius: $radius-sm;
  font-size: $font-size-xs;
  overflow: hidden;
  cursor: help;

  &:hover { border-color: rgba(var(--rc-accent), 0.65); }

  &.rc-fluid {
    flex: 1 1 0;
    width: auto;
    min-width: 0;
    max-width: 220px;
  }

  // Planète pleine : bascule en warning (ambre) pour alerter d'un coup d'œil.
  &.is-full {
    --rc-accent: 251, 191, 36;
  }
}

.pc-icon {
  flex-shrink: 0;
  font-size: 16px;
  color: rgba(var(--rc-accent), 0.95);
}

.rc-amount {
  color: $text-primary;
  flex: 1 1 auto;
  text-align: right;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1;
}

.tt-head-icon {
  font-size: 18px;
  color: $color-success;
}
</style>
